import { Box } from '@mui/material';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import Attention from 'assets/icons/Attention.svg?react';
import { useSelector } from 'react-redux';
import { selectMemberCodeDetails } from 'selectors/memberCodeSelector';
import { MemberCodeOutputStatusEnum } from 'pco-product-service-internal-consumer';
import { palette } from 'theme/palette';
import styled from '@emotion/styled';
import { StepIconProps as MuiStepIconProps } from '@mui/material/StepIcon';

export const StyledStepConnector = styled(StepConnector)(
  () =>
    ({
      [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 8,
        left: 'calc(-100% + 16px)',
        right: 'calc(90% + 16px)',
        textAlign: 'start',
      },
      [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
          borderColor: palette.oceanblue.main,
          borderWidth: '5px',
        },
      },
      [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
          borderColor: palette.oceanblue.main,
          borderWidth: '5px',
        },
      },
      [`& .${stepConnectorClasses.disabled}`]: {
        [`& .${stepConnectorClasses.line}`]: {
          borderColor: 'red',
          borderWidth: '0px',
        },
      },
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#eaeaf0',
        borderRadius: 1,
        borderWidth: '5px',
      },
    }) as unknown as TemplateStringsArray,
);

const styles = {
  root: {
    width: '60%',
  },
};

export const StepIconRoot = styled('div')(() => ({
  color: '#eaeaf0',
  display: 'flex',
  alignItems: 'center',
  zIndex: 1,
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: 'currentColor',
  '&.active': {
    color: palette.oceanblue.main,
  },
  '&.completed': {
    color: `${palette.oceanblue.main} !important`,
    fontSize: 18,
  },
  '&.error': {
    color: '#b5b5b5',
  },
  // ...(props.error && {
  //   color: '#b5b5b5',
  // },
  // props.completed && {
  //   color: palette.oceanblue.main,
  //   fontSize: 18,
  // },
  // props.active && {
  //   color: palette.oceanblue.main,
  // }),
  // '& .StepIcon-completedIcon': {
  //   color: '#784af4',
  //   zIndex: 1,
  //   fontSize: 18,
  // },
  // '& .StepIcon-circle': {
  //   width: 8,
  //   height: 8,
  //   borderRadius: '50%',
  //   backgroundColor: 'red',
  // },
}));

export const StepIcon = (props: MuiStepIconProps) => {
  return (
    <StepIconRoot
      className={`${props.active ? 'active' : ''}${props.completed ? 'completed' : ''}${props.error ? 'error' : ''}`}
    >
      <Box>{props.icon === 3 ? <Attention /> : <Box />}</Box>
    </StepIconRoot>
  );
};

export default function MemberCodeStatusStepper() {
  const memberCodeDetails = useSelector(selectMemberCodeDetails);
  // We have 4 cases to represent:
  // 1. to represent status: 'SENT' ==> activeStep = 0, codeExpired = false, step1 error = false, step2 error = false
  // 2. to represent status: 'ACTIVATED' ==> activeStep = 1, codeExpired = false, step1 error = false, step2 error = false
  // 3. to represent status: 'SENT' ==> activeStep = 0, codeExpired = true , step1 error = true, step2 error = false
  // 4. to represent status: 'SENT' ==> activeStep = 0, codeExpired = true , step1 error = true, step2 error = true

  const activeStep = memberCodeDetails?.status === MemberCodeOutputStatusEnum.Activated ? 1 : 0;
  const hasCodeExpired = memberCodeDetails?.status === MemberCodeOutputStatusEnum.Expired;
  const isCodeRevoked = memberCodeDetails?.status === MemberCodeOutputStatusEnum.Revoked;

  const stepLabelStyle = {
    alignItems: 'flex-start',
  };

  return (
    <Box sx={styles.root}>
      <Stepper
        style={{ padding: 0, margin: 0 }}
        alternativeLabel
        activeStep={activeStep}
        connector={<StyledStepConnector />}
      >
        <Step key={'Code sent'} disabled={false} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <StepLabel error={hasCodeExpired || isCodeRevoked} StepIconComponent={StepIcon} style={stepLabelStyle}>
            <Box style={{ width: '100%', textAlign: 'start' }}>
              <Typography variant={'body1'} style={{ fontWeight: 600, marginTop: '-16px' }}>
                Code sent
              </Typography>
              <Typography variant={'caption'}>
                {moment(memberCodeDetails?.generationDate).format('DD.MM.YYYY')}
              </Typography>
            </Box>
          </StepLabel>
        </Step>
        <Step key={'Activated'} disabled={false} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <StepLabel error={isCodeRevoked} StepIconComponent={StepIcon} style={stepLabelStyle}>
            <Box style={{ width: '100%', textAlign: 'start' }}>
              <Typography variant={'body1'} style={{ fontWeight: 600, marginTop: '-16px' }}>
                Activated
              </Typography>
              <Typography variant={'caption'}>
                {memberCodeDetails?.status === MemberCodeOutputStatusEnum.Activated ||
                memberCodeDetails?.status === MemberCodeOutputStatusEnum.Revoked
                  ? moment(memberCodeDetails?.activationDate).format('DD.MM.YYYY')
                  : '-'}
              </Typography>
            </Box>
          </StepLabel>
        </Step>
        {(hasCodeExpired || isCodeRevoked) && (
          <Step key={'Code expired'} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <StepLabel StepIconComponent={StepIcon} style={stepLabelStyle}>
              <Box style={{ width: '100%', textAlign: 'start' }}>
                <Typography variant={'body1'} style={{ fontWeight: 600, marginTop: '-16px' }}>
                  {memberCodeDetails?.status}
                </Typography>
                <Typography variant={'caption'}>
                  {hasCodeExpired
                    ? moment(memberCodeDetails?.expirationDate).format('DD.MM.YYYY')
                    : moment(memberCodeDetails?.revocationDate).format('DD.MM.YYYY')}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        )}
      </Stepper>
    </Box>
  );
}
